import { zodResolver } from "@hookform/resolvers/zod";
import { InfoCircle, Mail01, User01 } from "@untitledui/icons";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod/v3";
import { useUpdateMe } from "@/api/me";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useAuth } from "@/providers/auth-provider";
import { success } from "@/providers/ToastProvider";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";

const schema = z.object({
  firstName: z.string().trim().min(1, "Enter your first name."),
  lastName: z.string().trim().min(1, "Enter your last name."),
});

type FormValues = z.infer<typeof schema>;

const Divider = () => <div aria-hidden className="mx-6 border-t border-gray-200" />;

export const MyDetailsPage = () => {
  const { user } = useAuth();
  const updateMe = useUpdateMe();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: { firstName: user?.firstName ?? "", lastName: user?.lastName ?? "" },
    resetOptions: { keepDirtyValues: true },
  });
  const { isDirty } = form.formState;

  const save = (values: FormValues) => {
    updateMe.mutate(values, {
      onSuccess: (saved) => {
        form.reset(saved);
        success("Details saved");
      },
    });
  };

  const nameField = (name: keyof FormValues, label: string, autoComplete: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <Input
          className="min-w-[200px] flex-1"
          label={label}
          autoComplete={autoComplete}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={!!fieldState.error}
          hint={fieldState.error?.message}
        />
      )}
    />
  );

  return (
    <div className="flex h-screen w-full flex-col items-center overflow-y-auto bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-8 px-6 pt-16 pb-8 lg:px-16`}>
        <div className="flex flex-col gap-1">
          <h1 className="mb-1 flex flex-row items-center gap-2 text-xl font-normal text-secondary">
            <User01 className="h-5 w-5" strokeWidth={1.8} />
            My Details
          </h1>
          <span className="text-sm text-quaternary">Your name, email and profile photo</span>
        </div>

        <form
          className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white"
          onSubmit={form.handleSubmit(save)}
          noValidate
        >
          <section className="p-6">
            <h2 className="text-base font-medium text-gray-900">Name</h2>
            <div className="mt-5 flex max-w-[560px] flex-wrap gap-4">
              {nameField("firstName", "First name", "given-name")}
              {nameField("lastName", "Last name", "family-name")}
            </div>
          </section>
          <Divider />
          <section className="p-6">
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-medium text-gray-900">Email</h2>
              <Tooltip title="This is not editable. If you would like to change it, contact support at support@unbox-commerce.com">
                <TooltipTrigger
                  aria-label="Why can't I edit my email?"
                  className="inline-flex cursor-help text-gray-400 transition-colors duration-100 hover:text-gray-600"
                >
                  <InfoCircle className="size-4" aria-hidden />
                </TooltipTrigger>
              </Tooltip>
            </div>
            <Input
              className="mt-5 max-w-[280px]"
              aria-label="Email"
              type="email"
              icon={Mail01}
              value={user?.email ?? ""}
              isReadOnly
            />
          </section>
          {isDirty && (
            <>
              <Divider />
              <section className="flex flex-wrap items-center justify-end gap-3 p-6">
                <Button type="button" color="secondary" onClick={() => form.reset()}>
                  Cancel
                </Button>
                <Button type="submit" isLoading={updateMe.isPending}>
                  Save changes
                </Button>
              </section>
            </>
          )}
        </form>
      </div>
    </div>
  );
};
